import { CustomsDeclarationSchema } from "@redotech/redo-model/fulfillments/customs-declaration";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import {
  FulfillmentGroupDataSchema,
  FulfillmentInfoSchema,
  fulfillmentServiceSchema,
  GroupSelectedRateSchema,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentGroupUpdateSource } from "@redotech/redo-model/fulfillments/fulfillment-group-audit";
import { OMSEventSchema } from "@redotech/redo-model/fulfillments/fulfillment-group-events";
import { PrintStatusSchema } from "@redotech/redo-model/fulfillments/fulfillment-print-status";
import { Hazmat } from "@redotech/redo-model/fulfillments/hazmat";
import { OMSUserSchema } from "@redotech/redo-model/fulfillments/oms-user";
import {
  ItemParcelAssignmentsSchema,
  PackingParcelsSchema,
} from "@redotech/redo-model/fulfillments/packing-parcels";
import {
  SelectedShippingRateSetBy,
  SelectedShippingRateSetByInfoSchema,
} from "@redotech/redo-model/fulfillments/selected-shipping-rate-set-by";
import {
  ContentsType,
  NonDeliveryOption,
  USPSExemptionCode,
} from "@redotech/redo-model/outbound-labels/customs";
import {
  ShipmentPaymentOptionSchema,
  SpecialRateEligibility,
} from "@redotech/redo-model/outbound-labels/outbound-labels";
import { OutboundParcelSchema } from "@redotech/redo-model/outbound-labels/parcel";
import { WeightSchema } from "@redotech/redo-model/outbound-labels/util";
import { TagSchema } from "@redotech/redo-model/tag";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

const FulfillmentGroupUpdateSchema = z.object({
  fulfillmentGroupId: z.string(),
  source: createEnum(FulfillmentGroupUpdateSource).nullish(),
  selectedParcels: z.array(OutboundParcelSchema).nullish(),
  selectedShippingRate: GroupSelectedRateSchema.nullish(),
  displayWeightGrams: z.number().nullish(),
  assignedUser: OMSUserSchema.nullish(),
  tags: z.array(TagSchema).nullish(),
  noteFromBuyer: z.string().nullish(),
  noteToBuyer: z.string().nullish(),
  internalNote: z.string().nullish(),
  printStatus: PrintStatusSchema.nullish(),
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  // @deprecated - use selectedParcels insurance instead
  includeInsurance: z.boolean().nullish(),
  // @deprecated - use selectedParcels insurance instead
  insuredAmount: z.number().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility).nullish(),
  overrideDeliveryMethod: createEnum(RedoShopifyDeliveryMethodType).nullish(),
  scanVerified: z
    .object({
      verifiedBy: OMSUserSchema.nullish(),
      verifiedAt: z.date().nullish(),
    })
    .nullish(),
  packingSlipTemplateId: z.string().nullish(),
  newEvent: OMSEventSchema.nullish(),
  /** @deprecated */
  customsDeclarations: z.array(CustomsDeclarationSchema).nullish(),
  /** @deprecated */
  contentsType: createEnum(ContentsType).nullish(),
  /** @deprecated */
  contentsExplanation: z.string().nullish(),
  /** @deprecated */
  additionalCustomsComments: z.string().nullish(),
  /** @deprecated */
  nonDeliveryOption: createEnum(NonDeliveryOption).nullish(),
  /** @deprecated */
  uspsExemptionCode: createEnum(USPSExemptionCode).nullish(),
  /** @deprecated */
  itn: z.string().nullish(),
  /** @deprecated */
  taxIdentifierIds: z.array(z.string()).nullish(),
  ddp: z.boolean().nullish(),
  /** @deprecated */
  hazmat: createEnum(Hazmat).nullish(),
  saturdayDelivery: z.boolean().nullish(),
  assignedFulfillmentService: fulfillmentServiceSchema.nullish(),
  /** @deprecated */
  dryIceWeight: WeightSchema.nullish(),
  fulfillmentInfo: FulfillmentInfoSchema.nullish(),
  buyReturnLabel: z.boolean().nullish(),
  shipmentPaymentOption: ShipmentPaymentOptionSchema.nullish(),
  carbonNeutral: z.boolean().nullish(),
  directDeliveryOnly: z.boolean().nullish(),
  overrideResidentialAddress: z.boolean().nullish(),
  personalizedSlipTemplateId: z.string().nullish(),
  selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
    enumName: "SelectedShippingRateSetBy",
  }).nullish(),
  selectedShippingRateSetByInfo: SelectedShippingRateSetByInfoSchema.nullish(),
  packingParcels: PackingParcelsSchema.nullish(),
  itemParcelAssignments: ItemParcelAssignmentsSchema.nullish(),
  parcelsFinalized: z.boolean().nullish(),
});

export type FulfillmentGroupUpdate = z.infer<
  typeof FulfillmentGroupUpdateSchema
>;

export interface IFulfillmentGroupUpdate
  extends z.ZodType<FulfillmentGroupUpdate> {}

export const updateFulfillmentGroups = {
  name: "updateFulfillmentGroups" as const,
  input: z.object({
    updates: z.array(FulfillmentGroupUpdateSchema as IFulfillmentGroupUpdate),
  }),
  output: z.record(z.string(), FulfillmentGroupDataSchema),
};
